
import React, { useMemo } from 'react';
import { AppState, Staff, Shift } from '../types';
import { LOCATIONS } from '../constants';

interface DashboardProps {
  state: AppState;
  staff: Staff[];
  shifts: Shift[];
  onNavigate: (view: AppState['view'], locationId?: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ state, onNavigate }) => {
  // Mock status assignments for demonstration
  const siteStatuses: Record<string, 'fully' | 'lone' | 'cover' | 'inactive'> = {
    'h-hw': 'fully',
    'h-wel-h': 'fully',
    'h-wel-hmo': 'lone',
    'h-ste-h': 'fully',
    'h-she': 'cover',
    'h-hf': 'fully',
    'h-cha': 'lone',
    'h-and': 'fully',
    'h-lut': 'inactive',
    'h-bed': 'inactive',
    'yp-bs': 'fully',
    'yp-hod': 'cover',
    'yp-hem': 'lone',
    'yp-aca': 'fully',
    'yp-ste': 'fully',
  };

  const housingSites = useMemo(() => LOCATIONS.filter(l => l.type === 'Housing'), []);
  const ypSites = useMemo(() => LOCATIONS.filter(l => l.type === 'YP Services'), []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'fully': return 'bg-green-500';
      case 'lone': return 'bg-amber-500';
      case 'cover': return 'bg-red-500';
      default: return 'bg-gray-300';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'fully': return 'FULLY STAFFED';
      case 'lone': return 'LONE STAFF';
      case 'cover': return 'REQUIRES COVER';
      case 'inactive': return 'NOT IN SERVICE';
      default: return 'UNKNOWN';
    }
  };

  return (
    <div className="space-y-8 md:space-y-12 pb-16 px-2 md:px-0">
      {/* Dashboard Header */}
      <div className="bg-white border-b-4 md:border-b-8 border-black pt-4 md:pt-8 pb-8 md:pb-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
          <div className="space-y-2">
            <span className="text-ymca-orange font-bold text-[10px] md:text-xs uppercase tracking-[0.3em]">One YMCA UnityForce</span>
            <h1 className="ymca-brand-font text-5xl md:text-7xl lg:text-8xl leading-none uppercase">DASHBOARD</h1>
            <p className="text-gray-600 max-w-3xl text-sm md:text-lg font-medium leading-relaxed">
              Global operational oversight across all One YMCA divisions and sites.
            </p>
          </div>
          <div className="flex gap-4 w-full md:w-auto">
             <div className="bg-black text-white px-6 md:px-8 py-3 md:py-4 text-center flex-1 md:flex-initial">
                <p className="ymca-brand-font text-3xl md:text-5xl leading-none">91%</p>
                <p className="text-[8px] md:text-[10px] font-bold uppercase tracking-widest mt-1 opacity-60">Global Coverage</p>
             </div>
          </div>
        </div>
      </div>

      {/* Operational Site Status */}
      <div className="space-y-10">
        <div className="section-header">
          <h2 className="ymca-brand-font text-4xl md:text-5xl uppercase leading-none">HOUSING SITES STATUS</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {housingSites.map(site => (
            <SiteStatusCard 
              key={site.id} 
              site={site} 
              status={siteStatuses[site.id]} 
              color={getStatusColor(siteStatuses[site.id])}
              label={getStatusLabel(siteStatuses[site.id])}
              onClick={() => onNavigate('rota', site.id)}
            />
          ))}
        </div>

        <div className="section-header pt-8">
          <h2 className="ymca-brand-font text-4xl md:text-5xl uppercase leading-none">YP SERVICES STATUS</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {ypSites.map(site => (
            <SiteStatusCard 
              key={site.id} 
              site={site} 
              status={siteStatuses[site.id]} 
              color={getStatusColor(siteStatuses[site.id])}
              label={getStatusLabel(siteStatuses[site.id])}
              onClick={() => onNavigate('rota', site.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const SiteStatusCard = ({ site, status, color, label, onClick }: any) => (
  <div 
    onClick={onClick}
    className={`p-5 border-2 border-black bg-white group transition-all duration-300 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] cursor-pointer ${status === 'inactive' ? 'opacity-50' : ''}`}
  >
    <div className="flex justify-between items-start mb-4">
      <div className={`w-3 h-3 rounded-none ${color}`}></div>
      <span className="text-[7px] md:text-[9px] font-black uppercase tracking-widest text-gray-400">{site.address}</span>
    </div>
    <h3 className="ymca-brand-font text-2xl uppercase leading-none mb-1 truncate" title={site.name}>{site.name}</h3>
    <p className={`text-[8px] md:text-[10px] font-bold tracking-widest uppercase mt-2 ${status === 'cover' ? 'text-red-500' : status === 'lone' ? 'text-amber-500' : status === 'fully' ? 'text-green-500' : 'text-gray-400'}`}>
      {label}
    </p>
  </div>
);

export default Dashboard;
